import type {
  AddChemicalEntryLinkBody,
  AddChemicalHazardLinkBody,
  AdminChemicalEntryView,
  AdminChemicalHazardView,
  AdminChemicalLinkHistoryRow,
  AdminChemicalLinksResponse,
  ApiResponse,
  RemoveChemicalEntryLinkBody,
  RemoveChemicalHazardLinkBody,
  SetChemicalEntryStatusBody,
} from '@safetyhub/shared';
import api from './api';

/**
 * **화학물질 연결 편집** API 클라이언트 — 「화학물질 연결(국소배기 49종)」 페이지(`/chemical-links`)가 쓴다.
 * base `/admin/chemical-links`. 계약 정본은 shared `features/chemical-links.ts`와 서버 `AdminChemicalLinksController` JSDoc. 권한은
 * 카탈로그와 같은 `legal-obligation-catalog`.
 * ⚠️ 순수 판정·라벨은 두지 않는다(화면 spec이 이 모듈을 `vi.mock`한다 — 선례 `legalObligationCatalog.ts`).
 */
function unwrapEnvelope<T>(envelope: ApiResponse<T>): T {
  if (envelope.data === null) throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
  return envelope.data;
}

const BASE = '/admin/chemical-links';

export const CHEMICAL_LINKS_QUERY_KEYS = {
  all: ['admin', 'chemical-links'] as const,
  history: (itemCode: string | null, hazardCode: string | null) => ['admin', 'chemical-links', 'history', itemCode ?? '', hazardCode ?? ''] as const,
};

export const chemicalLinksApi = {
  async get(): Promise<AdminChemicalLinksResponse> {
    const res = await api.get<ApiResponse<AdminChemicalLinksResponse>>(BASE);
    return unwrapEnvelope(res.data);
  },
  async addEntryLink(itemCode: string, body: AddChemicalEntryLinkBody): Promise<AdminChemicalEntryView> {
    const res = await api.post<ApiResponse<AdminChemicalEntryView>>(`${BASE}/entries/${encodeURIComponent(itemCode)}/links`, body);
    return unwrapEnvelope(res.data);
  },
  async removeEntryLink(itemCode: string, substanceId: string, body: RemoveChemicalEntryLinkBody): Promise<AdminChemicalEntryView> {
    const res = await api.post<ApiResponse<AdminChemicalEntryView>>(
      `${BASE}/entries/${encodeURIComponent(itemCode)}/links/${encodeURIComponent(substanceId)}/remove`,
      body,
    );
    return unwrapEnvelope(res.data);
  },
  async setEntryStatus(itemCode: string, body: SetChemicalEntryStatusBody): Promise<AdminChemicalEntryView> {
    const res = await api.patch<ApiResponse<AdminChemicalEntryView>>(`${BASE}/entries/${encodeURIComponent(itemCode)}/status`, body);
    return unwrapEnvelope(res.data);
  },
  async addHazardLink(hazardCode: string, body: AddChemicalHazardLinkBody): Promise<AdminChemicalHazardView> {
    const res = await api.post<ApiResponse<AdminChemicalHazardView>>(`${BASE}/hazards/${encodeURIComponent(hazardCode)}/links`, body);
    return unwrapEnvelope(res.data);
  },
  async removeHazardLink(hazardCode: string, substanceId: string, body: RemoveChemicalHazardLinkBody): Promise<AdminChemicalHazardView> {
    const res = await api.post<ApiResponse<AdminChemicalHazardView>>(
      `${BASE}/hazards/${encodeURIComponent(hazardCode)}/links/${encodeURIComponent(substanceId)}/remove`,
      body,
    );
    return unwrapEnvelope(res.data);
  },
  async history(filter: { itemCode?: string; hazardCode?: string }): Promise<AdminChemicalLinkHistoryRow[]> {
    const res = await api.get<ApiResponse<AdminChemicalLinkHistoryRow[]>>(`${BASE}/history`, { params: filter });
    return unwrapEnvelope(res.data);
  },
};
